import React, { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Formik } from 'formik';

import BasicButton from 'components/buttons/basicButton';
import { SearchableSelectType } from 'components/inputs/searchableSelect';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import { validationSchema } from '../constants';

import useFormulaBulkUpdateMutation from 'hooks/formulas/useFormulaBulkUpdateMutation';

import TopicListDropdown from '../TopicListDropdown';
import BasicTopicModal from './BasicTopicModal';

interface AssignTopicProps {
    handleClose: () => void;
    formulaIds: number[];
    isDisabled: boolean;
}

const AssignTopic: FC<React.PropsWithChildren<AssignTopicProps>> = ({
    handleClose,
    formulaIds,
    isDisabled,
}) => {
    const updateMutation = useFormulaBulkUpdateMutation();
    const { id } = useParams<{ id: string }>();

    const handleSubmit = useCallback(
        (values: { topic: string }) => {
            const data = formulaIds.map((id) => ({
                id: id,
                topic_id: values.topic,
            }));
            updateMutation.mutate(data, {
                onSuccess: () => {
                    handleClose();
                },
            });
        },
        [updateMutation, handleClose, formulaIds],
    );

    const initialValues = useMemo(() => {
        return {
            topic: id,
        };
    }, [id]);

    return (
        <Formik
            initialValues={initialValues ?? {}}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnMount
            validateOnChange
            validationSchema={validationSchema}
        >
            {({ dirty, setFieldValue, values }) => {
                return (
                    <BasicTopicModal
                        header={'Assign to a topic'}
                        handleClose={handleClose}
                    >
                        <Container>
                            <TitleName>
                                Choose a Topic to move the Message Group(s) to:
                            </TitleName>
                            <TopicListDropdown
                                selectedTopicValue={values.topic}
                                handleChange={(field, value) =>
                                    setFieldValue(
                                        field,
                                        (value as SearchableSelectType).value,
                                    )
                                }
                                isDisabled={isDisabled}
                                menuPortalTarget={document.body}
                            />
                            <ActionContainer>
                                <BasicButton
                                    name="Cancel"
                                    isPrimary={false}
                                    action={handleClose}
                                />
                                <BasicButton
                                    name="Assign"
                                    type="submit"
                                    isPrimary={true}
                                    action={() => handleSubmit(values)}
                                    disabled={
                                        !dirty || !values.topic || isDisabled
                                    }
                                />
                            </ActionContainer>
                        </Container>
                    </BasicTopicModal>
                );
            }}
        </Formik>
    );
};

const Container = styled.div(() => ({
    backgroundColor: aampeTheme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: aampeTheme.borderRadius.medium,
    gap: aampeTheme.spacing4(4),
    height: '100%',
}));

const TitleName = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.neutral09,
});

const ActionContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

export default AssignTopic;
